import { useRouter } from 'expo-router';
import { AlertTriangle } from 'lucide-react-native';
import { Component, ReactNode } from 'react';
import { SafeAreaView } from 'react-native-safe-area-context';

import { Button, ButtonText } from '@/components/ui/button';
import { Heading } from '@/components/ui/heading';
import { HStack } from '@/components/ui/hstack';
import { Icon } from '@/components/ui/icon';
import { Text } from '@/components/ui/text';
import { VStack } from '@/components/ui/vstack';
import { Sentry } from '@/util/sentry';

type ChatErrorBoundaryProps = {
  children: ReactNode;
  conversationId?: string;
};

type ChatErrorBoundaryState = {
  hasError: boolean;
  error?: Error;
};

class ChatErrorBoundaryClass extends Component<
  ChatErrorBoundaryProps & { onReset: () => void; onBack: () => void },
  ChatErrorBoundaryState
> {
  constructor(props: ChatErrorBoundaryProps & { onReset: () => void; onBack: () => void }) {
    super(props);

    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error): ChatErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    Sentry.captureException(error, {
      tags: {
        component: 'ChatErrorBoundary',
        boundaryType: 'chat-screen',
      },
      extra: {
        errorMessage: error?.message,
        errorName: error?.name,
        errorStack: error?.stack,
        componentStack: errorInfo?.componentStack,
        conversationId: this.props.conversationId,
      },
    });
  }

  resetErrorBoundary = () => {
    this.setState({ hasError: false, error: undefined });

    this.props.onReset();
  };

  render() {
    if (this.state.hasError) {
      return (
        <SafeAreaView
          className="flex-1 items-center justify-center bg-background-0 px-5 py-8"
          edges={['bottom']}>
          <VStack className="max-w-md items-center" space="lg">
            <Icon as={AlertTriangle} size="xl" className="text-error-500" />

            <VStack className="items-center" space="sm">
              <Heading size="h1-xl" className="text-center">
                Something went wrong
              </Heading>

              <Text className="text-center text-typography-500">
                We encountered an unexpected error while loading the chat. Please try again or
                return to your inbox.
              </Text>
            </VStack>

            <HStack className="w-full" space="md">
              <Button variant="outline" className="flex-1" onPress={this.props.onBack}>
                <ButtonText>Back to Inbox</ButtonText>
              </Button>

              <Button className="flex-1" onPress={this.resetErrorBoundary}>
                <ButtonText>Try Again</ButtonText>
              </Button>
            </HStack>
          </VStack>
        </SafeAreaView>
      );
    }

    return this.props.children;
  }
}

export function ChatErrorBoundary({ children, conversationId }: ChatErrorBoundaryProps) {
  const router = useRouter();

  const handleBack = () => {
    router.replace('/inbox');
  };

  /**
   * Resets the error boundary and forces a fresh re-mount by replacing the route.
   * This ensures all hooks and queries re-initialize from scratch.
   */
  const handleReset = () => {
    if (conversationId) {
      router.replace(`/inbox/chat/${conversationId}`);
    }
  };

  return (
    <ChatErrorBoundaryClass
      conversationId={conversationId}
      onBack={handleBack}
      onReset={handleReset}>
      {children}
    </ChatErrorBoundaryClass>
  );
}
